import client from '../client';
import type { ShippingCarrier, ShippingAddress, CreateCarrierInput, CreateAddressInput, UpdateAddressInput } from '../../types/shipping';
import type { ApiResponse } from '../../types/api';

/**
 * Raw Torod location row. Torod uses different name keys per level
 * (country_name/country_name_ar for countries, name_ar for districts, etc.),
 * so this is intentionally a loose bag keyed by `id`. Use `torodLabel()` to
 * derive the display string rather than reading a specific field.
 */
export interface TorodLocation {
  id: number;
  country_name?: string;
  country_name_ar?: string;
  region_name?: string;
  region_name_ar?: string;
  city_name?: string;
  city_name_ar?: string;
  name?: string;
  name_en?: string;
  name_ar?: string;
  [key: string]: unknown;
}

export const shippingApi = {
  listCarriers: async (storeId: string) => {
    const res = await client.get<ApiResponse<{ carriers: ShippingCarrier[] }>>(
      `/dashboard/${storeId}/shipping/couriers`,
    );
    return res.data.data!;
  },

  addCarrier: async (storeId: string, data: CreateCarrierInput) => {
    await client.post(`/dashboard/${storeId}/shipping/carriers`, data);
  },

  updateCarrier: async (storeId: string, carrierId: string, data: Partial<CreateCarrierInput>) => {
    await client.patch(`/dashboard/${storeId}/shipping/carriers/${carrierId}`, data);
  },

  deleteCarrier: async (storeId: string, carrierId: string): Promise<void> => {
    await client.delete(`/dashboard/${storeId}/shipping/carriers/${carrierId}`);
  },

  activateCarrier: async (storeId: string, carrierId: string) => {
    await client.post(`/dashboard/${storeId}/shipping/carriers/${carrierId}/activate`);
  },

  /** Create a shipping order in Torod (required before getting carrier rates) */
  createShippingOrder: async (storeId: string, orderId: string, data?: { weight?: number; boxesCount?: number }) => {
    const res = await client.post<ApiResponse<{ orderId: string }>>(
      `/dashboard/${storeId}/shipping/orders/${orderId}/create`,
      data,
    );
    return res.data.data!;
  },

  createCustomCarrier: async (storeId: string, data: CreateCarrierInput) => {
    await client.post(`/dashboard/${storeId}/shipping/carriers`, data);
  },

  listAddresses: async (storeId: string) => {
    const res = await client.get<ApiResponse<{ addresses: ShippingAddress[] }>>(
      `/dashboard/${storeId}/shipping/addresses`,
    );
    return res.data.data!;
  },

  addAddress: async (storeId: string, data: CreateAddressInput) => {
    await client.post(`/dashboard/${storeId}/shipping/addresses`, data);
  },

  updateAddress: async (storeId: string, addressId: string, data: UpdateAddressInput) => {
    await client.patch(`/dashboard/${storeId}/shipping/addresses/${addressId}`, data);
  },

  deleteAddress: async (storeId: string, addressId: string): Promise<void> => {
    await client.delete(`/dashboard/${storeId}/shipping/addresses/${addressId}`);
  },

  printLabel: async (storeId: string, orderId: string) => {
    const res = await client.post<ApiResponse<{ labelUrl: string }>>(
      `/dashboard/${storeId}/shipping/print-label`,
      { orderId },
    );
    return res.data.data!;
  },

  listAvailableCarriers: async (storeId: string, params?: { orderId?: string; weight?: number; boxCount?: number }) => {
    const res = await client.get<ApiResponse<{ companies: Array<{
      id: number;
      title: string;
      title_arabic?: string;
      carrier_logo?: string;
      rate?: number;
      approximate_delivery_time_arabic?: string;
      active?: boolean;
      warning?: string;
      isAdded?: boolean;
      _source?: string;
    }> }>>(
      `/dashboard/${storeId}/shipping/available-carriers`,
      { params },
    );
    return res.data.data!;
  },

  createLabel: async (storeId: string, data: { orderId: string; carrierId: string; serviceType?: string; weight?: number; boxCount?: number }) => {
    const res = await client.post<ApiResponse<{ shippingLabel: string; trackingId?: string }>>(
      `/dashboard/${storeId}/shipping/labels`,
      data,
    );
    return res.data.data!;
  },

  // Torod location data. Torod returns level-specific Arabic/English keys (not a
  // generic `name`); resolve the display label with `torodLabel()` from formatters.
  getTorodCountries: async (storeId: string) => {
    const res = await client.get(`/dashboard/${storeId}/shipping/carriers/torod/getCountries`);
    return (res.data?.data ?? res.data) as Array<TorodLocation>;
  },

  getTorodRegions: async (storeId: string, countryId: number) => {
    const res = await client.get(`/dashboard/${storeId}/shipping/carriers/torod/getRegions`, { params: { country_id: countryId } });
    return (res.data?.data ?? res.data) as Array<TorodLocation>;
  },

  getTorodCities: async (storeId: string, regionId: number) => {
    const res = await client.get(`/dashboard/${storeId}/shipping/carriers/torod/getCities`, { params: { region_id: regionId } });
    return (res.data?.data ?? res.data) as Array<TorodLocation>;
  },

  getTorodDistricts: async (storeId: string, cityId: number) => {
    const res = await client.get(`/dashboard/${storeId}/shipping/carriers/torod/getDistricts`, { params: { cities_id: cityId } });
    return (res.data?.data ?? res.data) as Array<TorodLocation>;
  },

  downloadLabel: async (storeId: string, orderId: string) => {
    const res = await client.get(
      `/dashboard/${storeId}/shipping/labels/download`,
      { params: { orderId }, responseType: 'blob' },
    );
    const url = URL.createObjectURL(res.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `shipping-label-${orderId}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  },
};
